import BaseService from "./BaseService";
import type {
  CyclePeriodRecord,
  EntityId,
  ForageCyclePayload,
  ForageCycleRecord,
  PeriodValue,
} from "features/manejo/forageCycleManagementUtils";

const URL_BASE_V2 = `${process.env.REACT_APP_ENDPOINT}forragens/api/v2`;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isEntityId(value: unknown): value is EntityId {
  return typeof value === "number" || typeof value === "string";
}

function isPeriodValue(value: unknown): value is PeriodValue {
  return (
    value === undefined ||
    value === null ||
    typeof value === "number" ||
    typeof value === "string"
  );
}

function isCyclePeriodRecord(value: unknown): value is CyclePeriodRecord {
  if (!isRecord(value)) {
    return false;
  }
  const hasValidId =
    value.IdPeriodoCicloForragemFazenda === undefined ||
    value.IdPeriodoCicloForragemFazenda === null ||
    typeof value.IdPeriodoCicloForragemFazenda === "number";
  const hasValidLabel =
    value.Rotulo === undefined ||
    value.Rotulo === null ||
    typeof value.Rotulo === "string";
  const periodValueKeys = [
    "AlturaEntrada",
    "AlturaSaida",
    "DiaFim",
    "DiaInicio",
    "MesFim",
    "MesInicio",
    "QuantidadeDiasDescanso",
  ];
  return (
    hasValidId &&
    hasValidLabel &&
    periodValueKeys.every((key) => isPeriodValue(value[key]))
  );
}

function isCycleRecord(value: unknown): value is ForageCycleRecord {
  if (!isRecord(value)) {
    return false;
  }
  const hasValidId =
    value.IdCicloForragemFazenda === null ||
    typeof value.IdCicloForragemFazenda === "number";
  return (
    hasValidId &&
    isEntityId(value.IdFazenda) &&
    isEntityId(value.IdForragem) &&
    (value.quantidadePastosAtivos === undefined ||
      typeof value.quantidadePastosAtivos === "number") &&
    (value.PeriodoCicloForragemFazenda === undefined ||
      (Array.isArray(value.PeriodoCicloForragemFazenda) &&
        value.PeriodoCicloForragemFazenda.every(isCyclePeriodRecord)))
  );
}

function readCycleEnvelope(response: unknown): ForageCycleRecord {
  if (
    !isRecord(response) ||
    !isRecord(response.data) ||
    !isCycleRecord(response.data.data)
  ) {
    throw new Error("Resposta inválida ao salvar o ciclo de forragem.");
  }
  return response.data.data;
}

class ForragemCycleService extends BaseService {
  async obterCiclosForragemFazendaV2(
    idFazenda: EntityId
  ): Promise<ForageCycleRecord[]> {
    const response: unknown = await this.get(
      `${URL_BASE_V2}/ciclos-forragem-fazenda?IdFazenda=${encodeURIComponent(
        String(idFazenda)
      )}`
    );
    if (
      !isRecord(response) ||
      !isRecord(response.data) ||
      !Array.isArray(response.data.data) ||
      !response.data.data.every(isCycleRecord)
    ) {
      throw new Error("Resposta inválida ao consultar ciclos de forragem.");
    }
    return response.data.data;
  }

  async criarCicloForragemFazendaV2(
    ciclo: ForageCyclePayload
  ): Promise<ForageCycleRecord> {
    const response: unknown = await this.post(
      `${URL_BASE_V2}/ciclos-forragem-fazenda`,
      ciclo
    );
    return readCycleEnvelope(response);
  }

  async atualizarCicloForragemFazendaV2(
    idCiclo: EntityId,
    ciclo: ForageCyclePayload
  ): Promise<ForageCycleRecord> {
    const response: unknown = await this.put(
      `${URL_BASE_V2}/ciclos-forragem-fazenda/${encodeURIComponent(
        String(idCiclo)
      )}`,
      ciclo
    );
    return readCycleEnvelope(response);
  }
}

const forragemCycleService = new ForragemCycleService();

export default forragemCycleService;
